import * as React from "react";
import {
  Box,
  Button,
  Container,
  CssBaseline,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  ThemeProvider,
  Typography,
  createTheme,
} from "@mui/material";
import FiberManualRecordRoundedIcon from "@mui/icons-material/FiberManualRecordRounded";
import LandingBg from "../../assets/landingBg.png"; // ✅ Background image
import IpadImg from "../../assets/iPadPro3.png"; // ✅ Mockup image

import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { useNavigate } from "react-router-dom";

export default function WhyChooseJusttawk() {
  const navigate = useNavigate();

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          primary: { main: "#1e2a5a" },
          secondary: { main: "#f59e0b" },
          background: { default: "#f7f7fb" },
          text: { primary: "#0f172a", secondary: "#475569" },
        },
        shape: { borderRadius: 14 },
        typography: {
          fontFamily:
            "'Inter', system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial, 'Noto Sans', 'Apple Color Emoji', 'Segoe UI Emoji'",
          h2: { fontWeight: 800, lineHeight: 1.15, letterSpacing: "-0.02em" },
          body1: { lineHeight: 1.6 },
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: "none",
                fontWeight: 700,
                borderRadius: 999,
                paddingInline: 22,
              },
              sizeLarge: { paddingBlock: 12, paddingInline: 28, fontSize: 16 },
            },
          },
        },
      }),
    []
  );

  const benefits = [
    "Easy management of inbound campaigns.",
    "Hassle free 5 minute setup.",
    "Trusted by 100+ UAE Businesses",
    "Multichannel support: Voice, Chat, WhatsApp, Email",
    "24/7 Support in Arabic, English, Hindi, Urdu",
    "Easy Integration with Any CRM",
  ];

  const handleClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    navigate("/landing-page");
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        component="section"
        sx={{
          position: "relative",
          py: { xs: 6, md: 10 },
          backgroundImage: `linear-gradient(rgba(255,255,255,0.8), rgba(255,255,255,0.8)), url(${LandingBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          overflow: "hidden",
        }}
      >
        <Container maxWidth="xl">
          <Grid
            container
            spacing={{ xs: 4, md: 6 }}
            alignItems="center"
            sx={{
              pt: 4,
            }}
          >
            {/* Left Section */}
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                p: { xs: 3, md: 10 },
                zIndex: 2,
              }}
            >
              <Stack spacing={3} sx={{ px: { xs: 2, md: 5 } }}>
                <Typography
                  variant="h2"
                  component="h2"
                  sx={{
                    fontSize: { xs: 30, sm: 40, md: 46 },
                    color: "primary.main",
                  }}
                >
                  Why Businesses in Dubai
                  <br />
                  Choose{" "}
                  <Box component="span" sx={{ color: "secondary.main" }}>
                    Justtawk?
                  </Box>
                </Typography>

                <List dense sx={{ pt: 1, px: { xs: 0, md: 6 } }}>
                  {benefits.map((text) => (
                    <ListItem key={text} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 28, pt: "2px" }}>
                        <FiberManualRecordRoundedIcon
                          sx={{ color: "#606060", fontSize: 12 }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primaryTypographyProps={{
                          color: "#606060",
                          sx: {
                            fontSize: {
                              xs: 16,
                              md: 22,
                            },
                            fontWeight: 700,
                          },
                        }}
                        primary={text}
                      />
                    </ListItem>
                  ))}
                </List>

                <Box>
                  <Box
                    sx={{
                      backgroundColor: "#4b40d7",
                      color: "white",
                      borderRadius: 1,
                      px: {
                        xs: 3,
                        md: 7,
                      },
                      py: 1.5,
                      display: "inline-flex",
                      alignItems: "center",
                      cursor: "pointer",
                      fontWeight: 700,
                      fontSize: 16,
                      "&:hover": {
                        backgroundColor: "#3a33b0",
                      },
                    }}
                  >
                    <div onClick={handleClick}>
                      Try Now <ArrowOutwardIcon sx={{ ml: 1, fontSize: 20 }} />
                    </div>
                  </Box>
                </Box>
              </Stack>
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
              sx={{
                position: "relative",
              }}
            >
              <Box
                sx={{
                  position: { md: "absolute", xs: "static" },
                  right: { md: 0, xs: "auto" },
                  top: { md: "50%", xs: "auto" },
                  transform: { md: "translateY(-50%)", xs: "none" },
                  width: { xs: "100%", md: "90%" },
                  maxWidth: 800,
                  display: "flex",
                  justifyContent: { xs: "center", md: "flex-end" },
                  alignItems: "center",
                }}
              >
                <Box
                  component="img"
                  src={IpadImg}
                  alt="Justtawk dashboard preview"
                  sx={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                    objectFit: "contain",
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
